/* eslint-disable no-console */
import {EyeInvisibleOutlined, EyeTwoTone} from '@ant-design/icons';
import {Row, Col, Form, Input, Button, Checkbox, Modal, Tooltip} from 'antd';
import type {CheckboxChangeEvent} from 'antd/es/checkbox';
import Link from 'antd/es/typography/Link';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {CommonErrors} from '../../../constants/Common';
import {sendSignUpOTP} from '../../../services/modules/User/signUp';
import RegEx from '../../../utils/FormUtils/RegEx';
import {errorMessages} from '../Constants/errorMessages';
import {ISignupFormData} from '../Utils/Interfaces';

import {SignUpTermsPlaceholderContent} from './signUpTermsPlaceholderContent';

const TERMS_SCROLL_END_THRESHOLD_PX = 16;

interface TermsAcceptanceCheckboxProps {
  /** Injected by Form.Item when `valuePropName="checked"` */
  checked?: boolean;
  value?: boolean;
  onChange?: (e: CheckboxChangeEvent) => void;
  onOpenModal: () => void;
}

const TermsAcceptanceCheckbox: React.FC<TermsAcceptanceCheckboxProps> = ({
  checked: checkedFromForm,
  value,
  onChange,
  onOpenModal
}) => {
  const mergedChecked = !!(checkedFromForm ?? value);

  return (
    <Checkbox
      className="signup-terms-checkbox"
      checked={mergedChecked}
      onChange={(e) => {
        if (e.target.checked) {
          onOpenModal();
          const revert: CheckboxChangeEvent = {
            ...e,
            target: {...e.target, checked: false}
          };
          onChange?.(revert);
        } else {
          onChange?.(e);
        }
      }}
    >
      I have read and accept the{' '}
      {/* <Link
        href="https://www.bharatcarbon.earth/pages/terms-conditions.html"
        target="_blank"
        rel="noopener noreferrer"
        style={{color: '#626471', textDecoration: 'underline'}}
      > */}
      Terms and Conditions and{' '}
      <Link
        href="https://www.bharatcarbon.earth/pages/privacy-policy.html"
        target="_blank"
        rel="noopener noreferrer"
        style={{color: '#626471', textDecoration: 'underline'}}
      >
        Privacy Policy
      </Link>
    </Checkbox>
  );
};

interface SignUpFormProps {
  initialFromData: ISignupFormData;
  onFinish: (values: ISignupFormData) => void;
}

const SignUpScreen: React.FC<SignUpFormProps> = ({
  initialFromData,
  onFinish
}) => {
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const [buttonAction, setButtonAction] = useState<boolean>();
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [canAcceptTerms, setCanAcceptTerms] = useState(false);
  const termsScrollRef = useRef<HTMLDivElement>(null);

  const updateTermsScrollState = useCallback(() => {
    const el = termsScrollRef.current;
    if (!el) return;
    const {scrollTop, clientHeight, scrollHeight} = el;
    const atEnd =
      scrollTop + clientHeight >= scrollHeight - TERMS_SCROLL_END_THRESHOLD_PX;
    const fitsWithoutScroll =
      scrollHeight <= clientHeight + TERMS_SCROLL_END_THRESHOLD_PX;
    setCanAcceptTerms(atEnd || fitsWithoutScroll);
  }, []);

  const handleTermsContentReady = useCallback(() => {
    requestAnimationFrame(() => {
      updateTermsScrollState();
    });
  }, [updateTermsScrollState]);

  const handleFinish = async (values: ISignupFormData) => {
    setButtonAction(true);
    await sendSignUpOTP({
      businessName: values?.businessName,
      email: `${values?.email}`,
      businessWebsite: values?.businessWebsite,
      firstName: values?.firstName,
      lastName: values?.lastName
    })
      .then((data) => {
        setButtonAction(false);
        if (data?.status === 'error' && data?.errorType === 'BUSINESS') {
          const message = data.message;
          form.setFields([
            {
              name: 'businessWebsite',
              errors: [message]
            }
          ]);
        } else if (
          data?.status === 'error' &&
          data?.errorType === 'USER_EMAIL'
        ) {
          const message = data.message;
          form.setFields([
            {
              name: 'email',
              errors: [message]
            }
          ]);
        } else if (data.status === 'success') {
          const finalData = {
            ...values,
            email: `${values?.email}`
          };
          onFinish(finalData);
        }
      })
      .catch(() => {
        setButtonAction(false);
      });
  };

  const onValuesChange = () => {
    validateForm();
  };

  const validateForm = () => {
    const hasErrors = form
      .getFieldsError()
      .some(({errors}) => errors.length > 0);
    const allTouched = form.isFieldsTouched(true);
    const currentValues = form.getFieldsValue();
    const hasAcceptedTerms = currentValues.accept === true;
    setIsFormValid(!hasErrors && allTouched && hasAcceptedTerms);
  };

  const getSubmitTooltipTitle = (): string | undefined => {
    if (isFormValid) return undefined;
    const accept = form.getFieldValue('accept');
    if (accept !== true) {
      return 'Please read and accept the Terms and Conditions first.';
    }

    return 'Please complete all required fields.';
  };

  const handleModalAccept = () => {
    form.setFields([
      {
        name: 'accept',
        value: true,
        touched: true
      }
    ]);
    setTermsModalOpen(false);
    setCanAcceptTerms(false);
    validateForm();
  };

  const handleModalClose = () => {
    setTermsModalOpen(false);
    setCanAcceptTerms(false);
  };

  useEffect(() => {
    if (initialFromData) {
      const strEmail = initialFromData.email.trim();
      initialFromData = {
        ...initialFromData,
        email: strEmail
      };

      form.setFieldsValue(initialFromData);
    }
    validateForm();
  }, [initialFromData]);

  return (
    <>
      <Modal
        title={<span id="terms-modal-title">Terms and Conditions</span>}
        open={termsModalOpen}
        centered
        onCancel={handleModalClose}
        rootClassName="signup-terms-modal"
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 12,
              flexWrap: 'wrap'
            }}
          >
            <Button
              type="primary"
              disabled={!canAcceptTerms}
              onClick={handleModalAccept}
            >
              Accept Terms and Conditions
            </Button>
            <Button onClick={handleModalClose}>Close</Button>
          </div>
        }
        width="min(960px, 96vw)"
        destroyOnClose
        afterOpenChange={(open) => {
          if (open) {
            requestAnimationFrame(() => {
              updateTermsScrollState();
            });
          } else {
            setCanAcceptTerms(false);
          }
        }}
      >
        <p
          id="terms-modal-scroll-hint"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0
          }}
        >
          Scroll to the end of the document to enable Accept T&amp;C.
        </p>
        <div
          ref={termsScrollRef}
          className="signup-terms-scroll signup-terms-modal-scroll"
          onScroll={updateTermsScrollState}
          tabIndex={0}
          role="region"
          aria-labelledby="terms-modal-title"
          aria-describedby="terms-modal-scroll-hint"
        >
          <SignUpTermsPlaceholderContent
            onContentReady={handleTermsContentReady}
          />
        </div>
      </Modal>

      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => {
          void handleFinish(values);
        }}
        onValuesChange={onValuesChange}
        onFieldsChange={() => {
          validateForm();
        }} // to catch blur validations
      >
        {/* <Form.Item
          name="businessWebsite"
          label="Business Website"
          rules={[
            {
              async validator(_, value) {
                if (!value || value.trim() === '') {
                  await new Promise((resolve) => setTimeout(resolve, 0));
                  throw new Error(
                    `${errorMessages.requiredBusinessWebsiteUrl}`
                  );
                }

                if (value.length < 5) {
                  await new Promise((resolve) => setTimeout(resolve, 0));
                  throw new Error(
                    errorMessages.requiredBusinessWebsiteMoreThan4Characters
                  );
                }

                if (value.length > 320) {
                  await new Promise((resolve) => setTimeout(resolve, 0));
                  throw new Error(
                    errorMessages.requiredBusinessWebsiteNotMoreThan320Characters
                  );
                }

                const trimmedValue = value.trim();
                const fullUrl = `https://${trimmedValue}`;

                if (!RegEx.website.test(fullUrl)) {
                  await new Promise((resolve) => setTimeout(resolve, 0));
                  throw new Error(errorMessages.requiredValidBusinessWebsite);
                }

                await Promise.resolve();
              }
            }
          ]}
          required={false}
          validateTrigger={['onBlur', 'onChange']}
        >
          <Input addonBefore="https://" placeholder="yourcompany.com" />
        </Form.Item> */}

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="firstName"
              label="First name"
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory('First name')
                },
                {
                  max: 100,
                  message:
                    errorMessages.requiredFirstNameNotMoreThan100Characters
                },
                {
                  min: 2,
                  message: errorMessages.requiredFirstNameMoreThan1Characters
                },
                {
                  pattern: RegEx.names,
                  message: errorMessages.firstNameNotAllowedSpecialCharacters
                }
              ]}
              required={false}
              validateTrigger={['onBlur', 'onChange']}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="lastName"
              label="Last name"
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory('Last name')
                },
                {
                  max: 100,
                  message:
                    errorMessages.requiredLastNameNotMoreThan100Characters
                },
                {
                  pattern: RegEx.names,
                  message: errorMessages.lastNameNotAllowedSpecialCharacters
                }
              ]}
              required={false}
              validateTrigger={['onBlur', 'onChange']}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="email"
          label="Email address"
          rules={[
            {
              validator: async (_, value) => {
                if (!value || value.trim() === '') {
                  throw new Error(`${errorMessages.requiredEmail}`);
                }
                const fullEmail = `${value || ''}`;
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                await Promise.resolve();

                if (!emailRegex.test(fullEmail)) {
                  throw new Error(`${errorMessages.invalidEmail}`);
                }
              }
            }
          ]}
          required={false}
          validateTrigger={['onBlur', 'onChange']}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="password"
          label="Password"
          rules={[
            {required: true, message: `${errorMessages.requiredPassword}`},
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
              message: `${errorMessages.invalidPassword}`
            }
          ]}
          required={false}
          validateTrigger={['onBlur', 'onChange']}
        >
          <Input.Password
            iconRender={(visible) =>
              visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="Confirm Password"
          dependencies={['password']}
          rules={[
            {
              required: true,
              message: `${errorMessages.requiredConfirmPassword}`
            },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
              message: `${errorMessages.invalidConfirmPassword}`
            },
            ({getFieldValue}) => ({
              async validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return;
                }
                await new Promise((resolve) => setTimeout(resolve, 0)); // ensures at least one `await`
                throw new Error('Passwords do not match'); // cleaner than Promise.reject
              }
            })
          ]}
          required={false}
          validateTrigger={['onBlur', 'onChange']}
        >
          <Input.Password
            iconRender={(visible) =>
              visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
            }
          />
        </Form.Item>
        <Form.Item
          name="businessName"
          label="Business Name"
          rules={[
            {
              required: true,
              message: CommonErrors.mandatory('Business Name')
            },
            {
              max: 100,
              message:
                errorMessages.requiredBusinessNameNotMoreThan100Characters
            },
            {
              min: 3,
              message: errorMessages.requiredBusinessNameMoreThan2Characters
            },
            {
              pattern: RegEx.businessName,
              message: CommonErrors.noAllowedSpecChar
            },
            {
              whitespace: true,
              message: CommonErrors.mandatory('Business Name')
            }
          ]}
          required={false}
          validateTrigger={['onBlur', 'onChange']}
        >
          <Input />
        </Form.Item>

        <Form.Item name="subscribe" valuePropName="checked">
          <Checkbox>
            Yes, I want to receive emails about Bharat Carbon and related
            product and feature updates and promotions.
          </Checkbox>
        </Form.Item>

        <Form.Item
          name="accept"
          valuePropName="checked"
          required={false}
          validateTrigger={['onBlur', 'onChange']}
        >
          <TermsAcceptanceCheckbox
            onOpenModal={() => {
              setTermsModalOpen(true);
            }}
          />
        </Form.Item>

        <Form.Item>
          <Tooltip title={!isFormValid ? getSubmitTooltipTitle() : undefined}>
            <span style={{display: 'block', width: '100%'}}>
              <Button
                type="primary"
                htmlType="submit"
                block
                disabled={!isFormValid}
                style={{
                  backgroundColor: '#159a84',
                  borderRadius: 8,
                  height: 44,
                  fontWeight: 600
                }}
                loading={buttonAction}
              >
                {buttonAction ? 'Verifying' : 'Create Account'}
              </Button>
            </span>
          </Tooltip>

          <div className="or-sep">or</div>
          <div className="signup-link">
            Already have an account?
            <a id="anchor-signup" href="/">
              Login
            </a>
          </div>
        </Form.Item>
      </Form>
    </>
  );
};

export default SignUpScreen;
